import { Component, OnChanges, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { AppConstants } from '@unpispas/upp-defs';
import { languageService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';
import { i18nSupport } from '@unpispas/upp-data';

import { Product } from '@unpispas/upp-data';

import { TicketView } from '@unpispas/upp-data';
import { ProductView } from '@unpispas/upp-data';
import { PreselectView } from '@unpispas/upp-data';

import { PriceType, PriceInfo } from '@unpispas/upp-defs';

/** Guest catalog product / preselect row (photo, name, price, cart badge). */
@Component({
    selector: 'upp-guest-row',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-row.html',
    styleUrls: ['./upp-row.scss'],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestRowComponent extends i18nSupport implements OnChanges, OnDestroy {
    /** Product leaf or preselect shortcut (historical input name). */
    @Input() product: ProductView | PreselectView | null = null;
    @Input() ticket: TicketView | null = null;

    @Output() Select = new EventEmitter<ProductView | PreselectView>();

    private _product_subscription: Subscription | null = null;
    private _ticket_subscription: Subscription | null = null;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        public view: viewService,
    ) {
        super(lang, change.cdref);
    }

    ngOnChanges(): void {
        if (this._product_subscription) {
            this._product_subscription.unsubscribe();
            this._product_subscription = null;
        }

        if (this._ticket_subscription) {
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        if (this.product) {
            this._product_subscription = this.product.OnViewRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }

        if (this.ticket) {
            this._ticket_subscription = this.ticket.OnViewRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }
    }

    ngOnDestroy(): void {
        if (this._product_subscription) {
            this._product_subscription.unsubscribe();
            this._product_subscription = null;
        }

        if (this._ticket_subscription) {
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        super.OnDestroy();
    }

    /********************************/
    /* DISPLAY                      */
    /********************************/

    /** Parent product for a preselect; the leaf itself when the row is a product. */
    get DisplayProduct(): ProductView | null {
        if (!this.product) {
            return null;
        }

        if (this.product.ViewOf(Product)) {
            return this.product as ProductView;
        }

        return (this.product as PreselectView).product;
    }

    /** True when the row is a quick configuration hanging off a product. */
    get IsPreselect(): boolean {
        return !!this.product && !this.product.ViewOf(Product);
    }

    get RowPrice(): PriceType | null {
        if (!this.product || this.product.IsMarket) {
            return null;
        }

        const place = this.ticket?.place ?? null;
        const qrcode = this.ticket?.qrcode ?? null;

        if (this.product.ViewOf(Product)) {
            return (this.product as ProductView).MinPrice(place, qrcode);
        }

        const select = this.product as PreselectView;
        if (this.ticket) {
            const amount = select.tableprice(place, qrcode);
            const rate = select.product?.taxrate ?? AppConstants.defaultTaxRate;
            return new PriceInfo(amount, rate).price;
        }

        return select.priceinfo;
    }

    /**
     * Cart badge units: product total, or lines matching this preselect via IsDirect.
     */
    get InCartAmount(): number {
        if (!this.ticket || !this.product) {
            return 0;
        }

        if (this.product.ViewOf(Product)) {
            return this.ticket.AmountOfProduct(this.product as ProductView);
        }

        const select = this.product as PreselectView;
        let amount = 0;
        for (const line of this.ticket.products) {
            if (line.InCart && line.IsDirect(select)) {
                amount += line.amount || 0;
            }
        }

        return amount;
    }

    /********************************/
    /* HANDLERS                     */
    /********************************/

    OnSelect(): void {
        if (!this.product) {
            return;
        }

        this.Select.emit(this.product);
    }
}
